<template>
    <div class="flex flex-col w-full bg-white dark:bg-slate-800 rounded-lg shadow">
        <div class="relative mx-auto w-full h-36 overflow-hidden sm:h-96">
            <Link :href="postUrl" class="absolute z-10 inset-0" />
            <div class="aspect-w-10 aspect-h-5 rounded-t-lg shadow-inner overflow-hidden" v-html="post.featuredImageHtml"></div>
            <div v-if="post.isCompeting && !post.isVerified" class="group absolute left-3 top-3 flex items-center p-1 text-white font-display text-base font-bold bg-blue-600 bg-opacity-80 rounded-full shadow-lg uppercase space-x-3 sm:text-lg lg:p-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 lg:w-12 lg:h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                    />
                </svg>
                <span class="sr-only">You are competing</span>
            </div>

            <div v-if="post.isVerified" class="group absolute left-3 top-3 flex items-center p-1 text-white font-display text-base font-bold bg-green-600 bg-opacity-80 rounded-full shadow-lg uppercase space-x-3 sm:text-lg lg:p-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 lg:w-12 lg:h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                    />
                </svg>
                <span class="sr-only">Challenge Completed</span>
            </div>
        </div>

        <!-- Post Title & Content -->
        <div class="flex flex-row items-center justify-between px-5 py-4 space-x-3">
            <div class="group flex flex-col items-center px-2 text-pink-500 font-display text-2xl font-bold sm:left-5 sm:top-5 sm:px-5 sm:text-6xl">
                <span class="currency inline-flex leading-none">{{ post.pledgedAmount | abbr }}</span>
            </div>
            <!-- Post Details -->
            <div class="flex-1 mr-1 min-w-0 sm:mr-3">
                <Link :href="postUrl" class="line-clamp-3 pr-2 hover:text-gray-500 text-gray-700 dark:text-gray-200 dark:hover:text-indigo-200 text-sm" v-html="post.text"></Link>
            </div>

            <!-- Dropdown -->
            <div class="has-tooltip manual-tooltip relative flex shrink-0 self-start">
                <jet-dropdown align="right" width="48">
                    <template #trigger>
                        <button aria-expanded="false" aria-haspopup="true" class="flex items-center -m-px p-1 hover:text-blue-500 focus:text-blue-600 text-gray-500 hover:bg-gray-100 focus:bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500" type="button">
                            <span class="sr-only">Open options</span>
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                            </svg>
                        </button>
                    </template>

                    <template #content>
                        <div class="flex flex-col p-1 space-y-1">
                            <!-- Follow Post -->
                            <!--<Link v-if="!post.isChallenger" :href="post.favoriteUrl" :preserve-scroll="true" as="button" class="flex px-4 py-2 w-full hover:text-blue-500 text-gray-500 text-sm hover:bg-gray-100 rounded" method="post" role="menuitem">-->
                            <!--    &lt;!&ndash; Heroicon name: solid/star &ndash;&gt;-->
                            <!--    <svg :class="{ 'text-gray-400': !post.favorited, 'text-sky-500': post.favorited }" aria-hidden="true" class="mr-3 w-5 h-5" fill="currentColor" viewBox="0 0 20 20">-->
                            <!--        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />-->
                            <!--    </svg>-->
                            <!--    <span v-if="!post.favorited">Follow</span> <span v-if="post.favorited">Unfollow</span>-->
                            <!--</Link>-->

                            <Link
                                @click.prevent=";[copyToClipboard($event), $refs.copyTextTooltip.showTooltip($event)]"
                                :href="postUrl"
                                class="flex px-4 py-2 w-full hover:text-blue-500 text-gray-500 text-sm hover:bg-gray-100 rounded"
                                role="menuitem"
                            >
                                <svg class="mr-3 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                </svg>
                                <span>Copy Link</span>
                            </Link>

                            <!-- Edit Post -->
                            <!--<Link v-if="post.isChallenger" :href="route('posts.edit', post.id)" class="flex rounded px-4 py-2 text-sm text-gray-800 hover:bg-gray-100 hover:text-gray-900" role="menuitem">-->
                            <!--    <svg class="mr-3 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">-->
                            <!--        <path d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />-->
                            <!--    </svg>-->
                            <!--    <span>Edit</span>-->
                            <!--</Link>-->
                        </div>
                        <div class="p-1">
                            <!-- Delete Post -->
                            <a v-if="post.isChallenger" class="flex px-4 py-2 text-red-700 hover:text-red-900 text-sm hover:bg-red-100 rounded" href="#" role="menuitem" @click.prevent="confirmDeletePost">
                                <svg class="mr-3 w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                </svg>
                                <span>Archive</span>
                            </a>

                            <!-- Report Post -->
                            <!--<a v-if="!post.isChallenger" class="flex px-4 py-2 text-red-700 hover:text-red-900 text-sm hover:bg-red-100 rounded" href="#" role="menuitem" @click.prevent="reportingPost = true">-->
                            <!--    &lt;!&ndash; Heroicon name: solid/flag &ndash;&gt;-->
                            <!--    <svg aria-hidden="true" class="mr-3 w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">-->
                            <!--        <path clip-rule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" fill-rule="evenodd" />-->
                            <!--    </svg>-->
                            <!--    <span>Report</span>-->
                            <!--</a>-->
                            <report-post v-if="!post.isChallenger" :post="post" />
                        </div>
                    </template>
                </jet-dropdown>
                <tooltip ref="copyTextTooltip" background-color="bg-blue-500 border-blue-500 text-white">
                    <div class="flex items-center space-x-1">
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <div class="leading-none">copied</div>
                    </div>
                </tooltip>
            </div>
        </div>

        <div v-if="post.isCompeting && !post.isVerified" class="mb-3 mx-5 p-3 bg-blue-50 border-l-4 border-blue-400 rounded shadow-sm lg:p-4">
            <div class="flex items-center">
                <div class="flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                        />
                    </svg>
                </div>
                <div class="ml-3">
                    <p class="text-blue-700 text-xs lg:text-sm">You are competing in this challenge!</p>
                </div>
            </div>
        </div>

        <div v-if="post.isVerified" class="mb-3 mx-5 p-3 bg-green-50 border-l-4 border-green-400 rounded shadow-sm lg:p-4">
            <div class="flex items-center">
                <div class="flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                        />
                    </svg>
                </div>
                <div class="ml-3">
                    <p class="text-green-700 text-xs lg:text-sm">Challenge Completed!</p>
                </div>
            </div>
        </div>

        <!--Footer-->
        <div class="flex items-center justify-between px-5 py-2 bg-gray-50 dark:bg-slate-900/50 rounded-b-lg">
            <div class="flex items-center text-gray-600 dark:text-gray-200 text-sm leading-5 space-x-2">
                <!-- LikesLikeables -->
                <like :likeable="post"></like>

                <span class="text-gray-500 font-medium">&middot;</span>

                <!-- Comments -->
                <div class="has-tooltip">
                    <Link :href="postUrl" class="group flex items-center space-x-1">
                        <svg class="w-6 h-6 text-gray-500 dark:text-gray-200 group-hover:text-gray-700 dark:group-hover:text-indigo-200 font-medium" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" stroke-linecap="round" stroke-linejoin="round" stroke-width="1" />
                        </svg>
                        <div class="flex-shrink-0 group-hover:text-gray-700 dark:group-hover:text-indigo-200">{{ post.commentsCount }}</div>
                    </Link>
                    <tooltip v-if="post.commentsCount">Show {{ post.commentsCount | abbr }} {{ "comment" | pluralize(post.commentsCount) }}</tooltip>
                </div>

                <span class="font-medium">&middot;</span>

                <div class="has-tooltip cursor-default">
                    <time :datetime="post.publishedAt">
                        {{ post.publishedAt | when() }}
                    </time>
                    <tooltip>{{ formatDate(post.publishedAt, "lll") }}</tooltip>
                </div>
            </div>

            <!-- Share Post -->
            <button
                class="has-tooltip flex items-center -m-px p-2 hover:text-blue-500 focus:text-blue-600 text-gray-500 hover:bg-gray-100 focus:bg-gray-100 dark:hover:text-gray-100 dark:focus:text-white dark:text-gray-200 dark:hover:bg-slate-800 dark:focus:bg-slate-900 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-slate-800"
                @click="showShareModal = true"
            >
                <!-- Heroicon name: solid/share -->
                <svg class="w-5 h-5 transform-gpu -scale-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                </svg>
                <span class="sr-only">Share</span>
                <tooltip>Share</tooltip>
            </button>
        </div>

        <share-modal :post="post" :show.sync="showShareModal" />
    </div>
</template>

<script>
import JetDropdown from "@/Jetstream/Dropdown"
import JetDropdownLink from "@/Jetstream/DropdownLink"
import Avatar from "@/Jetstream/Avatar"
import Like from "@/Components/Like"
import JetSecondaryButton from "@/Jetstream/SecondaryButton"
import ImageModal from "@/Components/ImageModal"
import Tooltip from "@/Components/Tooltip"
import ShareModal from "@/Components/ShareModal"
import ReportPost from "@/Pages/Post/ReportPost"

export default {
    name: "FeedCard",

    components: {
        ReportPost,
        ShareModal,
        Tooltip,
        ImageModal,
        JetSecondaryButton,
        Like,
        Avatar,
        JetDropdown,
        JetDropdownLink,
    },

    props: {
        post: {
            default: {},
            type: Object,
        },
    },

    computed: {
        postUrl() {
            return this.route('posts.show', {
                username: this.post.anonymous ? 'anonymous' : this.post.user?.username ?? 'anonymous',
                post: this.post.id
            });
        },
        user() {
            return this.post.user
        },
        thumbs() {
            if (!this.post) return null
            return _.take(this.post.thumbs, this.showThumbCount)
        },

        thumbCount() {
            if (!this.post) return null
            return this.post.thumbs.length
        },

        hiddenThumbs() {
            if (!this.post) return null
            if (this.thumbCount < this.showThumbCount) return null
            return _.takeRight(this.thumbs, this.thumbCount - (this.showThumbCount - 1))
        },

        hiddenThumbCount() {
            if (!this.hiddenThumbs) return null
            return this.hiddenThumbs.length
        },
    },

    watch: {
        galleryThumbnail(thumbnail) {
            this.showGalleryThumbnail = !!thumbnail
        },

        showGalleryThumbnail(bool) {
            if (!bool) {
                this.galleryThumbnail = null
            }
        },
    },

    data() {
        return {
            deletingPost: null,
            showGalleryThumbnail: false,
            galleryThumbnail: null,
            showThumbCount: 6,
            showShareModal: false,
        }
    },

    methods: {
        isLastThumb(i) {
            return this.showThumbCount - 1 === i
        },

        confirmDeletePost() {
            this.$emit("confirm-delete", this.post)
        },
    },
}
</script>
